/**
 * React Query hooks for the unified `/api/cards` surface. Backs every board
 * (agent_task, product_item, account) — page-level features pass their
 * `boardType` and otherwise share a single client.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BoardType } from '@chatsdet/cards-plugins';
import { API_URL } from '../../../config';
import {
  moveInFlatLists,
  rollbackMove,
  type MoveSnapshot,
} from '../../boards/optimisticMove';

export type TagColor =
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'teal'
  | 'blue'
  | 'indigo'
  | 'purple'
  | 'pink'
  | 'gray';

export type CardTag = {
  id: string;
  orgId: string;
  name: string;
  color: TagColor;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
};

export type LinkedOrgRef = {
  id: string;
  name: string;
};

export type Card = {
  id: string;
  orgId: string;
  boardType: BoardType;
  title: string;
  description: string;
  stage: string;
  sortOrder: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  details: Record<string, unknown>;
  tags: CardTag[];
  /** Populated by the API only for boards with `linked_org` feature (account). */
  linkedOrg: LinkedOrgRef | null;
};

const cardsKey = (boardType: BoardType) => ['cards', boardType] as const;
const cardKey = (id: string) => ['cards', 'one', id] as const;
const orgTagsKey = ['org', 'tags'] as const;

async function getJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include', ...init });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Request failed (${res.status}): ${body}`);
  }
  return (await res.json()) as T;
}

export function useCards(boardType: BoardType, options?: { search?: string }) {
  const search = options?.search?.trim();

  return useQuery({
    queryKey: [...cardsKey(boardType), { search }] as const,
    queryFn: async () => {
      // Server caps at 200; for kanban we want the full board so paginate
      // until we run out. In practice a single page covers most orgs.
      const out: Card[] = [];
      const pageSize = 200;
      let page = 1;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const params = new URLSearchParams({
          boardType,
          page: String(page),
          pageSize: String(pageSize),
        });
        if (search) params.set('search', search);
        const res = await getJson<{ ok: true; data: Card[] }>(`/cards?${params.toString()}`);
        out.push(...res.data);
        if (res.data.length < pageSize) break;
        page += 1;
        if (page > 25) break; // safety net: 5000 cards
      }
      return out;
    },
  });
}

export function useCard(id: string | null | undefined) {
  return useQuery({
    queryKey: cardKey(id ?? ''),
    enabled: !!id,
    queryFn: async () => {
      const res = await getJson<{ ok: true; data: Card }>(`/cards/${id}`);
      return res.data;
    },
  });
}

export function useCreateCard(boardType: BoardType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      title: string;
      description?: string;
      stage: string;
      details?: Record<string, unknown>;
    }) => {
      const res = await getJson<{ ok: true; data: Card }>(`/cards`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ boardType, ...input }),
      });
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cardsKey(boardType) });
    },
  });
}

export function useUpdateCard(boardType: BoardType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      title?: string;
      description?: string;
      stage?: string;
      sortOrder?: number;
      details?: Record<string, unknown>;
    }) => {
      const { id, ...body } = input;
      const res = await getJson<{ ok: true; data: Card }>(`/cards/${id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      return res.data;
    },
    onSuccess: (card) => {
      void qc.invalidateQueries({ queryKey: cardsKey(boardType) });
      void qc.invalidateQueries({ queryKey: cardKey(card.id) });
    },
  });
}

export function useMoveCard(boardType: BoardType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      stage: string;
      beforeItemId?: string | null;
      afterItemId?: string | null;
    }) => {
      const { id, ...body } = input;
      // Neighbor-based move: the server computes the fractional position
      // transactionally, so the optimistic order below survives the refetch.
      const res = await getJson<{ ok: true; data: Card }>(`/cards/${id}/move`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      return res.data;
    },
    onMutate: async (input): Promise<{ snapshot: MoveSnapshot }> => {
      // Prefix-match: list queries are keyed [...cardsKey, {search}] — an
      // exact-key setQueryData on cardsKey alone never hits any of them.
      await qc.cancelQueries({ queryKey: cardsKey(boardType) });
      const snapshot = moveInFlatLists<Card>(qc, cardsKey(boardType), {
        itemId: input.id,
        updateItem: (c) => ({ ...c, stage: input.stage }),
        target: {
          beforeItemId: input.beforeItemId ?? null,
          afterItemId: input.afterItemId ?? null,
        },
        getId: (c) => c.id,
        isSameColumn: (c) => c.stage === input.stage,
      });
      return { snapshot };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.snapshot) rollbackMove(qc, ctx.snapshot);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: cardsKey(boardType) });
    },
  });
}

export function useDeleteCard(boardType: BoardType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await getJson<{ ok: true }>(`/cards/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cardsKey(boardType) });
    },
  });
}

// ── Tags plugin ────────────────────────────────────────────────────────────
export function useOrgTags() {
  return useQuery({
    queryKey: orgTagsKey,
    queryFn: async () => {
      const res = await getJson<{ ok: true; data: CardTag[] }>(`/org/tags`);
      return res.data;
    },
  });
}

export function useCreateTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; color: TagColor; description?: string | null }) => {
      const res = await getJson<{ ok: true; tag: CardTag }>(`/org/tags`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      return res.tag;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgTagsKey });
    },
  });
}

// ── Linked-org plugin (account board) ─────────────────────────────────────
export function useSetCardLinkedOrg(boardType: BoardType, cardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (customerOrgId: string) => {
      const res = await getJson<{ ok: true; data: LinkedOrgRef }>(
        `/cards/${cardId}/linked-org`,
        {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ customerOrgId }),
        }
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cardsKey(boardType) });
      void qc.invalidateQueries({ queryKey: cardKey(cardId) });
    },
  });
}

export function useUnlinkCardOrg(boardType: BoardType, cardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await getJson<{ ok: true }>(`/cards/${cardId}/linked-org`, { method: 'DELETE' });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cardsKey(boardType) });
      void qc.invalidateQueries({ queryKey: cardKey(cardId) });
    },
  });
}

export function useSetCardTags(boardType: BoardType, cardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tagIds: string[]) => {
      await getJson<{ ok: true }>(`/org/tags/entity`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ entityType: 'card', entityId: cardId, tagIds }),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cardsKey(boardType) });
      void qc.invalidateQueries({ queryKey: cardKey(cardId) });
    },
  });
}

// ── Comments plugin ────────────────────────────────────────────────────────
export type CardComment = {
  id: string;
  cardId: string;
  authorId: string;
  body: string;
  createdAt: string;
  updatedAt: string;
};

const commentsKey = (cardId: string) => ['cards', 'comments', cardId] as const;

export function useCardComments(cardId: string | null | undefined) {
  return useQuery({
    queryKey: commentsKey(cardId ?? ''),
    enabled: !!cardId,
    queryFn: async () => {
      const res = await getJson<{ ok: true; data: CardComment[] }>(`/cards/${cardId}/comments`);
      return res.data;
    },
  });
}

export function useCreateCardComment(cardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: string) => {
      const res = await getJson<{ ok: true; data: CardComment }>(`/cards/${cardId}/comments`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: commentsKey(cardId) });
    },
  });
}

// ── Checklist plugin ───────────────────────────────────────────────────────
export type CardChecklistItem = {
  id: string;
  cardId: string;
  text: string;
  checked: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  checkedAt: string | null;
  checkedById: string | null;
};

const checklistKey = (cardId: string) => ['cards', 'checklist', cardId] as const;

export function useChecklistItems(cardId: string | null | undefined) {
  return useQuery({
    queryKey: checklistKey(cardId ?? ''),
    enabled: !!cardId,
    queryFn: async () => {
      const res = await getJson<{ ok: true; data: CardChecklistItem[] }>(
        `/cards/${cardId}/checklist-items`
      );
      return res.data;
    },
  });
}

export function useCreateChecklistItem(cardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (text: string) => {
      const res = await getJson<{ ok: true; data: CardChecklistItem }>(
        `/cards/${cardId}/checklist-items`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ text }),
        }
      );
      return res.data;
    },
    onSuccess: (item) => {
      const key = checklistKey(cardId);
      const previous = qc.getQueryData<CardChecklistItem[]>(key);
      if (previous) {
        qc.setQueryData<CardChecklistItem[]>(key, [...previous, item]);
      } else {
        void qc.invalidateQueries({ queryKey: key });
      }
    },
  });
}

export function useUpdateChecklistItem(cardId: string) {
  const qc = useQueryClient();
  const key = checklistKey(cardId);
  return useMutation({
    mutationFn: async (input: { id: string; text?: string; checked?: boolean }) => {
      const { id, ...body } = input;
      const res = await getJson<{ ok: true; data: CardChecklistItem }>(
        `/cards/${cardId}/checklist-items/${id}`,
        {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      return res.data;
    },
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<CardChecklistItem[]>(key);
      if (previous) {
        qc.setQueryData<CardChecklistItem[]>(
          key,
          previous.map((it) =>
            it.id === input.id
              ? {
                  ...it,
                  ...(input.text !== undefined ? { text: input.text } : {}),
                  ...(input.checked !== undefined ? { checked: input.checked } : {}),
                }
              : it
          )
        );
      }
      return { previous };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useDeleteChecklistItem(cardId: string) {
  const qc = useQueryClient();
  const key = checklistKey(cardId);
  return useMutation({
    mutationFn: async (id: string) => {
      await getJson<{ ok: true }>(`/cards/${cardId}/checklist-items/${id}`, {
        method: 'DELETE',
      });
      return id;
    },
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<CardChecklistItem[]>(key);
      if (previous) {
        qc.setQueryData<CardChecklistItem[]>(
          key,
          previous.filter((it) => it.id !== id)
        );
      }
      return { previous };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useReorderChecklistItem(cardId: string) {
  const qc = useQueryClient();
  const key = checklistKey(cardId);
  return useMutation({
    mutationFn: async (input: {
      id: string;
      beforeId?: string | null;
      afterId?: string | null;
      // Optimistic-only payload — server recomputes sortOrder.
      orderedIds: string[];
    }) => {
      const res = await getJson<{ ok: true; data: CardChecklistItem }>(
        `/cards/${cardId}/checklist-items/${input.id}/reorder`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            beforeId: input.beforeId ?? null,
            afterId: input.afterId ?? null,
          }),
        }
      );
      return res.data;
    },
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<CardChecklistItem[]>(key);
      if (previous) {
        const byId = new Map(previous.map((it) => [it.id, it]));
        const reordered = input.orderedIds
          .map((id) => byId.get(id))
          .filter((it): it is CardChecklistItem => !!it);
        qc.setQueryData<CardChecklistItem[]>(key, reordered);
      }
      return { previous };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
    },
  });
}
